import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Users, Monitor, ScanLine } from "lucide-react";
import { Brand, LanguageToggle } from "./Brand";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

export function AppHeader({ activeItem }) {
  const { user, tenant, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const items = [];
  const perms = user?.permissions || [];
  if (user?.role === "super_admin") {
    items.push({ to: "/super-admin", icon: LayoutDashboard, label: t("super_admin"), key: "super" });
  }
  if (user?.role === "tenant_admin" || user?.role === "agent") {
    if (perms.includes("dashboard")) {
      items.push({ to: "/admin", icon: LayoutDashboard, label: t("dashboard"), key: "admin" });
    }
    if (perms.includes("staff")) {
      items.push({ to: "/staff", icon: Users, label: t("staff"), key: "staff" });
    }
  }
  if (tenant?.slug) {
    if (perms.includes("kiosk") || user?.role === "super_admin") {
      items.push({ to: `/kiosk/${tenant.slug}`, icon: ScanLine, label: t("kiosk"), key: "kiosk" });
    }
    if (perms.includes("display") || user?.role === "super_admin") {
      items.push({ to: `/display/${tenant.slug}`, icon: Monitor, label: t("display"), key: "display" });
    }
  }

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <Brand />
          <nav className="hidden md:flex items-center gap-1">
            {items.map((it) => {
              const Icon = it.icon;
              const active = activeItem === it.key;
              return (
                <Link
                  key={it.key}
                  to={it.to}
                  data-testid={`nav-${it.key}`}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm transition ${
                    active
                      ? "bg-blue-50 text-blue-800"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {it.label}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          {user && (
            <div className="hidden sm:flex items-center gap-2 text-sm text-slate-700">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-800 grid place-items-center font-semibold">
                {user.name?.[0] ?? "U"}
              </div>
              <div className="leading-tight">
                <div className="font-medium text-slate-900">{user.name}</div>
                <div className="text-xs text-slate-500">{user.role}</div>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">{t("logout")}</span>
          </button>
        </div>
      </div>
    </header>
  );
}
